import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button as UIButton } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MapPin,
  Users,
  Eye,
  Clock,
  Smartphone,
  Monitor,
  Globe,
  TrendingUp,
  Activity,
  Search,
  MousePointer,
  AlertTriangle,
  RefreshCw,
  Download,
  BarChart3,
  PieChart,
  LineChart,
  Target,
} from "lucide-react";

interface AnalyticsStats {
  totalSessions: number;
  totalEvents: number;
  totalPageViews: number;
  uniqueVisitors: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  popularPages: Array<{ page: string; views: number }>;
  deviceStats: {
    types: Record<string, number>;
    os: Record<string, number>;
    browsers: Record<string, number>;
  };
  locationStats: Array<{ location: string; count: number }>;
  trafficSources: {
    utm: Record<string, number>;
    referrers: Record<string, number>;
  };
  userJourneys: Array<{
    sessionId: string;
    pageViews: string[];
    startTime: number;
    duration: number;
    device: string;
    location: string;
  }>;
  conversions: any[];
  searches: any[];
  errors: any[];
  realTimeVisitors: number;
}

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/analytics");
      const data = await response.json();
      setStats(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const exportData = async () => {
    try {
      const response = await fetch("/api/analytics");
      const data = await response.json();
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tigola-analytics-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export data:", error);
    }
  };

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Website Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Comprehensive tracking and insights for tigola.com
              </p>
              {lastUpdated && (
                <p className="text-sm text-gray-500 mt-1">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <UIButton
                onClick={loadAnalytics}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </UIButton>
              <UIButton onClick={exportData} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </UIButton>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Real-time Visitors
              </CardTitle>
              <Activity className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats?.realTimeVisitors || 0}
              </div>
              <p className="text-xs text-muted-foreground">Currently online</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Sessions
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalSessions || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{stats?.sessionsToday || 0} today
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Page Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.totalPageViews || 0}
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Unique Visitors
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.uniqueVisitors || 0}
              </div>
              <p className="text-xs text-muted-foreground">Different users</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analytics Tabs */}
        <Tabs defaultValue="traffic" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="traffic">UTM Sources</TabsTrigger>
            <TabsTrigger value="locations">Locations</TabsTrigger>
            <TabsTrigger value="pages">Pages</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="devices">Devices</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
          </TabsList>

          {/* Popular Pages */}
          <TabsContent value="pages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Most Popular Pages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.popularPages?.map((page, index) => (
                    <div
                      key={page.page}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-semibold">{page.page}</h3>
                          <p className="text-sm text-gray-600">Page URL</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">{page.views}</div>
                        <div className="text-xs text-gray-500">views</div>
                      </div>
                    </div>
                  )) || <p>No page data available</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Journeys */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Recent User Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.userJourneys?.slice(0, 10).map((journey) => (
                    <div
                      key={journey.sessionId}
                      className="p-4 border rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{journey.device}</Badge>
                          <span className="text-sm text-gray-600">
                            {journey.location}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(journey.startTime)} •{" "}
                          {formatDuration(journey.duration)}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {journey.pageViews.map((page, idx) => (
                          <Badge
                            key={idx}
                            variant="secondary"
                            className="text-xs"
                          >
                            {page}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )) || <p>No user journey data available</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Device Statistics */}
          <TabsContent value="devices">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5" />
                    Device Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.types || {}).map(
                      ([type, count]) => (
                        <div
                          key={type}
                          className="flex items-center justify-between"
                        >
                          <span className="capitalize">{type}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    Operating Systems
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.os || {}).map(
                      ([os, count]) => (
                        <div
                          key={os}
                          className="flex items-center justify-between"
                        >
                          <span>{os}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Browsers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats?.deviceStats.browsers || {}).map(
                      ([browser, count]) => (
                        <div
                          key={browser}
                          className="flex items-center justify-between"
                        >
                          <span>{browser}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ),
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Location Statistics */}
          <TabsContent value="locations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Visitor Locations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats?.locationStats?.map((location, index) => (
                    <div
                      key={location.location}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="font-medium">{location.location}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">
                          {location.count}
                        </div>
                        <div className="text-xs text-gray-500">visitors</div>
                      </div>
                    </div>
                  )) || <p>No location data available</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Traffic Sources */}
          <TabsContent value="traffic">
            <div className="space-y-6">
              {/* UTM Source Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    App Download Sources (UTM Tracking)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(stats?.trafficSources.utm || {}).length >
                  0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(stats.trafficSources.utm)
                        .sort(([, a], [, b]) => b - a)
                        .map(([source, count]) => {
                          const sourceLabels: Record<string, string> = {
                            website: "Website",
                            facebook: "Facebook Organic",
                            facebook_ads: "Facebook Ads",
                            instagram: "Instagram Organic",
                            instagram_ads: "Instagram Ads",
                            tiktok_ads: "TikTok Ads",
                            google_organic: "Google Organic",
                            google_ads: "Google Ads",
                            google_maps: "Google Maps",
                            google_maps_ads: "Google Maps Ads",
                            whatsapp: "WhatsApp Organic",
                            whatsapp_ads: "WhatsApp Ads",
                            business_card: "Business Card QR",
                            poster: "Shop Poster QR",
                            car_door: "Car Door Sign QR",
                            window_sticker: "Car Window Sticker",
                            flyer: "Flyers",
                          };

                          const label =
                            sourceLabels[source] || source.replace("_", " ");

                          return (
                            <div
                              key={source}
                              className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-gray-900">
                                  {label}
                                </h3>
                                <Badge
                                  variant="secondary"
                                  className="text-lg font-bold"
                                >
                                  {count}
                                </Badge>
                              </div>
                              <div className="text-sm text-gray-600">
                                Source: {source}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {(
                                  (count /
                                    Object.values(
                                      stats.trafficSources.utm,
                                    ).reduce((a, b) => a + b, 0)) *
                                  100
                                ).toFixed(1)}
                                % of total
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No UTM tracking data yet
                      </p>
                      <p className="text-gray-400 text-sm">
                        Use the test links below to generate sample data
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Traditional Traffic Sources */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Website Sources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(stats?.trafficSources.referrers || {})
                        .sort(([a], [b]) => {
                          // Sort "direct app link" to the top
                          if (a === "direct app link") return -1;
                          if (b === "direct app link") return 1;
                          return 0;
                        })
                        .map(([referrer, count]) => {
                          const isAppLink = referrer === "direct app link";
                          const displayName = isAppLink
                            ? "Direct App Link"
                            : referrer.charAt(0).toUpperCase() +
                              referrer.slice(1);
                          const totalReferrers = Object.values(
                            stats?.trafficSources.referrers || {},
                          ).reduce((a, b) => a + b, 0);
                          const percentage =
                            totalReferrers > 0
                              ? ((count / totalReferrers) * 100).toFixed(1)
                              : "0.0";

                          return (
                            <div
                              key={referrer}
                              className={`flex items-center justify-between p-3 rounded ${
                                isAppLink
                                  ? "bg-blue-50 border border-blue-200"
                                  : ""
                              }`}
                            >
                              <div>
                                <div className="font-medium">{displayName}</div>
                                <div className="text-xs text-gray-600">
                                  {percentage}% of traffic
                                </div>
                              </div>
                              <Badge
                                variant={isAppLink ? "default" : "secondary"}
                              >
                                {count}
                              </Badge>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Total UTM Sources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary">
                          {Object.values(
                            stats?.trafficSources.utm || {},
                          ).reduce((a, b) => a + b, 0)}
                        </div>
                        <div className="text-sm text-gray-600">
                          Total app downloads tracked
                        </div>
                      </div>
                      <div className="pt-4 border-t">
                        <div className="text-sm text-gray-600">
                          Unique sources:{" "}
                          {Object.keys(stats?.trafficSources.utm || {}).length}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Events */}
          <TabsContent value="events">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Search Queries
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.searches?.length ? (
                    <div className="space-y-2">
                      {stats.searches.slice(0, 10).map((search, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded">
                          <span className="text-sm">{search.data?.query}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No search data</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Conversions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.conversions?.length ? (
                    <div className="space-y-2">
                      {stats.conversions.slice(0, 10).map((conversion, idx) => (
                        <div key={idx} className="p-2 bg-green-50 rounded">
                          <div className="text-sm font-medium">
                            {conversion.data?.conversionType}
                          </div>
                          {conversion.data?.value && (
                            <div className="text-xs text-gray-600">
                              £{conversion.data.value}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No conversion data</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Errors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.errors?.length ? (
                    <div className="space-y-2">
                      {stats.errors.slice(0, 10).map((error, idx) => (
                        <div key={idx} className="p-2 bg-red-50 rounded">
                          <div className="text-sm text-red-800">
                            {error.data?.message}
                          </div>
                          <div className="text-xs text-red-600">
                            {error.page}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No errors logged</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
